import { Loader2 } from 'lucide-react';
import type { ShelfInventory } from '@/types/api';
import { EntityLink } from './EntityLink';

interface StockTableProps {
  items: ShelfInventory[];
  isLoading?: boolean;
  showLocation?: boolean;
  showProduct?: boolean;
  emptyMessage?: string;
}

export function StockTable({
  items,
  isLoading,
  showLocation = true,
  showProduct = true,
  emptyMessage = 'No stock on record.',
}: StockTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600 dark:text-warm-sand">
        <Loader2 className="w-5 h-5 animate-spin text-amber-450 mr-2" />
        Loading stock…
      </div>
    );
  }

  if (!items.length) {
    return <p className="text-gray-600 dark:text-warm-sand py-8 text-center">{emptyMessage}</p>;
  }

  return (
    <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table w-full min-w-[600px]">
          <thead className="bg-gray-100 dark:bg-charcoal-700/50">
            <tr>
              {showProduct && (
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase">Product</th>
              )}
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase">Variant</th>
              {showLocation && (
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase">Location</th>
              )}
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase">On Shelf</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase">Available</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase">Bin</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-charcoal-700">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-charcoal-700/30">
                {showProduct && (
                  <td className="px-4 py-3">
                    <EntityLink
                      type="product"
                      id={item.productId}
                      label={item.productName || 'Unknown'}
                    />
                  </td>
                )}
                <td className="px-4 py-3 text-gray-900 dark:text-warm-cream mono text-sm">{item.variantSku}</td>
                {showLocation && (
                  <td className="px-4 py-3">
                    <EntityLink type="location" id={item.locationId} label={item.locationName} />
                  </td>
                )}
                <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-warm-cream">{item.quantityOnShelf}</td>
                <td className="px-4 py-3 text-right text-green-600 dark:text-green-400">{item.availableQuantity}</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-warm-sand">
                  {[item.binLocation, item.binNumber].filter(Boolean).join(' / ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
